import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { Building2, Leaf, Zap, CheckCircle2, FileDown, ShieldCheck } from "lucide-react";

export function CompaniesWall() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/companies/wall").then((r) => setRows(r.data)).catch(() => {}); }, []);

  return (
    <div className="mx-auto max-w-5xl px-6 py-16">
      <div className="mono text-xs text-primary">// companies · verify wall</div>
      <h1 className="mt-2 text-5xl font-black tracking-tighter">Companies shipping ideas.</h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">Every card is a company that has verified real implementation of a citizen's proposal. Ranked by measured CO₂ impact.</p>

      {rows.length === 0 ? (
        <div className="mt-10 border border-dashed border-border p-12 text-center text-muted-foreground">
          No companies have verified success yet. Company accounts can mark proposals as implemented from the proposal detail page.
        </div>
      ) : (
        <div className="mt-10 grid gap-px bg-border md:grid-cols-2">
          {rows.map((c) => (
            <Link key={c.company} to={`/companies-wall/${encodeURIComponent(c.company)}`} data-testid={`wall-${c.company}`} className="bg-background p-6 hard-hover">
              <div className="flex items-center gap-2 text-xs text-primary uppercase tracking-widest"><Building2 className="h-3 w-3" />{c.company}</div>
              <div className="mt-4 flex items-center gap-6">
                <StatChip icon={CheckCircle2} v={c.verified_count} label="Verified" />
                <StatChip icon={Leaf} v={`${c.total_co2_kg} kg`} label="CO₂ saved" />
                <StatChip icon={Zap} v={c.total_units} label="Units" />
              </div>
              {c.sample_titles?.length > 0 && (
                <ul className="mt-4 text-xs text-muted-foreground space-y-1">
                  {c.sample_titles.map((t, i) => <li key={`${t}-${i}`} className="line-clamp-1">· {t}</li>)}
                </ul>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function CompanyPage() {
  const { company } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/companies/wall/${encodeURIComponent(company)}`).then((r) => setData(r.data)); }, [company]);
  if (!data) return <div className="mx-auto max-w-4xl px-6 py-20 text-muted-foreground">Loading…</div>;

  return (
    <div className="mx-auto max-w-5xl px-6 py-16">
      <Link to="/companies-wall" className="mono text-xs text-muted-foreground hover:text-primary">← verify wall</Link>
      <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 text-primary text-xs uppercase tracking-widest"><Building2 className="h-4 w-4" />Verified implementer</div>
          <h1 className="mt-2 text-5xl font-black tracking-tighter">{data.company}</h1>
        </div>
        <a
          data-testid="download-signed-report"
          href={`${BACKEND_URL}/api/companies/wall/${encodeURIComponent(data.company)}/report.html`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 border border-primary bg-primary/10 hover:bg-primary/20 text-primary px-4 py-2 text-xs uppercase tracking-widest"
        >
          <FileDown className="h-3 w-3" /><ShieldCheck className="h-3 w-3" />Download signed impact report
        </a>
      </div>

      <div className="mt-8 grid gap-px bg-border sm:grid-cols-3">
        <BigStat icon={CheckCircle2} v={data.verified_count} label="Ideas implemented" />
        <BigStat icon={Leaf} v={`${data.total_co2_kg} kg`} label="CO₂ saved / year" />
        <BigStat icon={Zap} v={data.total_units} label="Units deployed" />
      </div>

      <div className="mt-12">
        <div className="mb-4 text-xs uppercase tracking-widest text-primary">Verified proposals</div>
        <div className="grid gap-px bg-border md:grid-cols-2">
          {data.proposals.map((p) => (
            <Link key={p.id} to={`/proposals/${p.id}`} className="bg-background p-5 hard-hover">
              <div className="inline-flex items-center gap-1 border border-primary bg-primary text-primary-foreground px-2 py-0.5 text-[9px] uppercase tracking-widest">
                <CheckCircle2 className="h-3 w-3" /> Verified
              </div>
              <h3 className="mt-3 font-bold tracking-tight line-clamp-2">{p.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{p.idea}</p>
              <div className="mt-3 flex items-center gap-4 text-xs">
                <span className="text-muted-foreground">by {p.author_name}</span>
                {p.co2_saved_kg != null && <span className="mono text-primary">{p.co2_saved_kg} kg CO₂</span>}
                {p.units_deployed != null && <span className="mono text-primary">{p.units_deployed} units</span>}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

const StatChip = ({ icon: Icon, v, label }) => (
  <div className="flex items-center gap-2">
    <Icon className="h-4 w-4 text-primary" />
    <div>
      <div className="mono text-primary font-bold text-sm">{v}</div>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  </div>
);

const BigStat = ({ icon: Icon, v, label }) => (
  <div className="bg-background p-6">
    <Icon className="h-5 w-5 text-primary" />
    <div className="mt-4 mono text-3xl font-black text-primary">{v}</div>
    <div className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
  </div>
);
